import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { TOAST_ICON_TYPE } from '@/components/Elements';
import { API_STATUS } from '@/constants';
import { COMMON_ERROR_DIALOG_CODE } from '@/constants/error';
import { VIEW_CONFIG_STATUS } from '@/constants/firestore';
import {
  useViewUpdateApi,
  VIEW_UPDATE_API_RESULT_CODE,
  ViewUpdateApiRequest,
  ViewUpdateApiResponse,
} from '@/features/api';
import { useNeedAgreement } from '@/features/expired';
import { useCommonErrorDialog } from '@/hooks/useCommonErrorDialog';
import { useToastInfo } from '@/hooks/useToastInfo';

/**
 * 一時停止/再開ボタン用カスタムフック
 *
 * @returns
 */
export const useShareResumeButton = () => {
  const { addToastMessage } = useToastInfo();
  const { t } = useTranslation();
  const { updateView, viewUpdateState, resetViewUpdateState } =
    useViewUpdateApi();
  const { needAgreement } = useNeedAgreement();
  const { setCommonErrorDialogType } = useCommonErrorDialog();

  /**
   * 再開ボタン押下時の処理
   */
  const clickResumeButton = useCallback(() => {
    const request: ViewUpdateApiRequest = {
      status: VIEW_CONFIG_STATUS.ACTIVE,
    };
    // 共有画面更新API呼び出し
    updateView(request);
  }, [updateView]);

  /**
   * 共有画面更新APIの成功時の処理
   */
  const updateViewSuccess = useCallback(() => {
    // Firestore上の共有の一時停止/再開の変更を検知したタイミングでReduxを更新するので、ここではAPIリセットのみ行う
    // 共有停止/再開ボタンが配置されているヘッダーが常に表示されているので明示的にリセットする必要がある
    resetViewUpdateState();
  }, [resetViewUpdateState]);

  /**
   * 共有画面更新APIの失敗時の処理
   */
  const updateViewFailed = useCallback(
    (apiResponse: ViewUpdateApiResponse | undefined) => {
      // 共有画面情報更新APIの呼び出し状況をリセット
      // 共有停止/再開ボタンが配置されているヘッダーが常に表示されているので明示的にリセットする必要がある
      resetViewUpdateState();
      // アクセス期限切れエラー
      if (apiResponse?.resultCode === VIEW_UPDATE_API_RESULT_CODE.WARN_AUTH) {
        setCommonErrorDialogType(COMMON_ERROR_DIALOG_CODE.ACCESS_ERROR);

        return;
      }

      // PTID期限切れエラー
      if (
        apiResponse?.resultCode ===
        VIEW_UPDATE_API_RESULT_CODE.INFO_EXPIRED_IDTOKEN
      ) {
        setCommonErrorDialogType(COMMON_ERROR_DIALOG_CODE.PTID_ERROR);

        return;
      }

      // INFO_NEED_AGREEMENTの場合、利用規約画面に遷移
      if (
        apiResponse?.resultCode ===
        VIEW_UPDATE_API_RESULT_CODE.INFO_NEED_AGREEMENT
      ) {
        needAgreement();

        return;
      }

      addToastMessage(
        t('sharePauseResumeButton.共有の再開に失敗しました'),
        TOAST_ICON_TYPE.WARNING,
      );
    },
    [
      addToastMessage,
      needAgreement,
      resetViewUpdateState,
      setCommonErrorDialogType,
      t,
    ],
  );

  /**
   * 共有画面更新APIの呼び出し状況を監視
   */
  useEffect(() => {
    if (
      viewUpdateState.status === API_STATUS.IDLE ||
      viewUpdateState.status === API_STATUS.LOADING
    ) {
      return;
    }

    // 成功
    if (
      viewUpdateState.status === API_STATUS.SUCCESS &&
      viewUpdateState.data?.resultCode === VIEW_UPDATE_API_RESULT_CODE.OK
    ) {
      updateViewSuccess();

      return;
    }

    updateViewFailed(viewUpdateState.data);
  }, [updateViewFailed, updateViewSuccess, viewUpdateState]);

  return {
    clickResumeButton,
    viewUpdateState,
  };
};
